// assets/product-personalization.js
class ProductPersonalization extends HTMLElement {
  constructor() {
    super();
    this.currentType = null;
    this.isPersonalizationEnabled = false;
    this.hasUserInput = false;
  }

  connectedCallback() {
    this.initializeComponent();
    this.setupEventListeners();
    this.syncHiddenInputs();
  }

  initializeComponent() {
    // Get all elements
    this.toggleNo = this.querySelector('[data-personalization-toggle][value="no"]');
    this.toggleYes = this.querySelector('[data-personalization-toggle][value="yes"]');
    this.optionsContainer = this.querySelector('[data-options-container]');
    
    // Get all type buttons
    this.typeButtons = this.querySelectorAll('[data-personalization-type]');
    
    // Get all input wrappers
    this.inputWrappers = this.querySelectorAll('[data-input-wrapper]');
    
    // Get all inputs
    this.inputs = this.querySelectorAll('[data-personalization-input]');
    
    // Get all counters
    this.counters = this.querySelectorAll('[data-counter]');
    
    // Get cart buttons
    this.addToCartButton = document.querySelector('.add-to-cart-button');
    this.paymentButton = document.querySelector('.shopify-payment-button');
    
    // Set initial state
    this.isPersonalizationEnabled = false;
    this.hideAllInputs();
    this.clearAllInputs();
  }

  setupEventListeners() {
    // Toggle listeners
    if (this.toggleNo) {
      this.toggleNo.addEventListener('change', () => this.handleToggleChange('no'));
    }
    
    if (this.toggleYes) {
      this.toggleYes.addEventListener('change', () => this.handleToggleChange('yes'));
    }

    // Type button listeners
    this.typeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const type = button.dataset.personalizationType;
        this.selectType(type, button);
      });
    });

    // Input listeners for character count and syncing
    this.inputs.forEach(input => {
      input.addEventListener('input', () => {
        const type = input.dataset.personalizationInput;
        this.updateCharacterCount(type, input.value.length);
        this.syncToHiddenInput(type, input.value);
        this.checkUserInput();
      });
      
      input.addEventListener('blur', () => {
        this.validateInput(input);
      });
    });
  }

  syncHiddenInputs() {
    // Clear all hidden inputs on initialization
    this.clearAllHiddenInputs();
  }

  syncToHiddenInput(type, value) {
    // Find the corresponding hidden input in the form
    const hiddenInput = document.querySelector(`[data-personalization-hidden="${type}"]`);
    
    if (hiddenInput) {
      // Only set value if personalization is enabled and this type is active
      const wrapper = this.querySelector(`[data-input-wrapper="${type}"]`);
      if (this.isPersonalizationEnabled && wrapper && !wrapper.hidden) {
        hiddenInput.value = value.trim();
      } else {
        hiddenInput.value = '';
      }
      
      // Debug log
      console.log(`Synced ${type}: "${value}" to hidden input`);
    }
  }

  clearAllHiddenInputs() {
    // Clear all hidden personalization inputs
    const hiddenInputs = document.querySelectorAll('[data-personalization-hidden]');
    hiddenInputs.forEach(input => {
      input.value = '';
    });
  }

  handleToggleChange(value) {
    if (value === 'yes') {
      this.isPersonalizationEnabled = true;
      this.showOptions();
      // Auto-select first type
      const firstButton = this.typeButtons[0];
      if (firstButton) {
        const firstType = firstButton.dataset.personalizationType;
        this.selectType(firstType, firstButton);
      }
    } else {
      this.isPersonalizationEnabled = false;
      this.hideOptions();
      this.clearAllInputs();
      this.clearAllHiddenInputs();
      this.hideAllInputs();
      this.updateButtonsState(true); // Enable buttons when personalization is off
    }
  }

  showOptions() {
    if (this.optionsContainer) {
      this.optionsContainer.hidden = false;
    }
  }

  hideOptions() {
    if (this.optionsContainer) {
      this.optionsContainer.hidden = true;
    }
  }

  selectType(type, button) {
    // Clear hidden inputs for other types
    this.typeButtons.forEach(btn => {
      if (btn !== button) {
        const otherType = btn.dataset.personalizationType;
        const hiddenInput = document.querySelector(`[data-personalization-hidden="${otherType}"]`);
        if (hiddenInput) {
          hiddenInput.value = '';
        }
      }
    });
    
    // Update button states
    this.typeButtons.forEach(btn => {
      btn.classList.remove('active');
    });
    button.classList.add('active');
    
    // Show/hide appropriate input
    this.currentType = type;
    this.hideAllInputs();
    this.showInput(type);
    
    // Sync current value if exists
    const currentInput = this.querySelector(`[data-personalization-input="${type}"]`);
    if (currentInput && currentInput.value) {
      this.syncToHiddenInput(type, currentInput.value);
    }
    
    // Check if there's content to control button state
    this.checkUserInput();
  }

  showInput(type) {
    const wrapper = this.querySelector(`[data-input-wrapper="${type}"]`);
    if (wrapper) {
      wrapper.hidden = false;
      // Focus on the input
      const input = wrapper.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  }

  hideAllInputs() {
    this.inputWrappers.forEach(wrapper => {
      wrapper.hidden = true;
    });
  }

  clearAllInputs() {
    this.inputs.forEach(input => {
      input.value = '';
      const type = input.dataset.personalizationInput;
      this.updateCharacterCount(type, 0);
    });
    this.hasUserInput = false;
  }

  updateCharacterCount(type, length) {
    const counter = this.querySelector(`[data-counter="${type}"]`);
    const input = this.querySelector(`[data-personalization-input="${type}"]`);
    
    if (counter && input) {
      const max = input.dataset.max || input.maxLength;
      counter.textContent = `${length}/${max}`;
      
      // Add warning class if near limit
      if (length >= max * 0.9) {
        counter.classList.add('near-limit');
      } else {
        counter.classList.remove('near-limit');
      }
    }
  }

  validateInput(input) {
    const isRequired = input.dataset.required === 'true';
    const wrapper = input.closest('[data-input-wrapper]');
    
    if (isRequired && this.isPersonalizationEnabled && wrapper && !wrapper.hidden) {
      if (!input.value.trim()) {
        input.classList.add('field__input--error');
        this.showError(input, 'Este campo é obrigatório');
      } else {
        input.classList.remove('field__input--error');
        this.clearError(input);
      }
    }
  }

  showError(input, message) {
    const wrapper = input.closest('.input-container');
    let errorElement = wrapper.querySelector('.field__message');
    
    if (!errorElement) {
      errorElement = document.createElement('span');
      errorElement.className = 'field__message field__message--error';
      wrapper.appendChild(errorElement);
    }
    
    errorElement.textContent = message;
  }

  clearError(input) {
    const wrapper = input.closest('.input-container');
    const errorElement = wrapper.querySelector('.field__message');
    if (errorElement) {
      errorElement.remove();
    }
  }

  checkUserInput() {
    let hasContent = false;
    
    this.inputs.forEach(input => {
      const type = input.dataset.personalizationInput;
      const wrapper = this.querySelector(`[data-input-wrapper="${type}"]`);
      
      // Check if this input is visible and has content
      if (wrapper && !wrapper.hidden && input.value.trim().length > 0) {
        hasContent = true;
      }
    });
    
    this.hasUserInput = hasContent;
    
    // Update buttons based on whether user has input
    if (this.isPersonalizationEnabled) {
      this.updateButtonsState(hasContent);
    }
  }

  updateButtonsState(enabled) {
    // Update add to cart button
    if (this.addToCartButton) {
      if (enabled) {
        this.addToCartButton.disabled = false;
        this.addToCartButton.style.opacity = '1';
        this.addToCartButton.style.cursor = 'pointer';
      } else {
        this.addToCartButton.disabled = true;
        this.addToCartButton.style.opacity = '0.5';
        this.addToCartButton.style.cursor = 'not-allowed';
      }
    }
    
    // Update payment button container
    if (this.paymentButton) {
      if (enabled) {
        this.paymentButton.style.opacity = '1';
        this.paymentButton.style.pointerEvents = 'auto';
        this.paymentButton.style.cursor = 'pointer';
        
        // Re-enable all buttons inside payment button container
        const buttons = this.paymentButton.querySelectorAll('button');
        buttons.forEach(btn => {
          btn.disabled = false;
          btn.style.opacity = '1';
          btn.style.cursor = 'pointer';
        });
      } else {
        this.paymentButton.style.opacity = '0.5';
        this.paymentButton.style.pointerEvents = 'none';
        this.paymentButton.style.cursor = 'not-allowed';
        
        // Disable all buttons inside payment button container
        const buttons = this.paymentButton.querySelectorAll('button');
        buttons.forEach(btn => {
          btn.disabled = true;
          btn.style.opacity = '0.5';
          btn.style.cursor = 'not-allowed';
        });
      }
    }
  }
}

// Register the custom element
if (!customElements.get('product-personalization-component')) {
  customElements.define('product-personalization-component', ProductPersonalization);
}

// Add debug helper to window for testing
if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
  window.debugPersonalization = () => {
    const hiddenInputs = document.querySelectorAll('[data-personalization-hidden]');
    console.log('=== Hidden Personalization Inputs ===');
    hiddenInputs.forEach(input => {
      console.log(`${input.name}: "${input.value}"`);
    });
    
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      const formData = new FormData(form);
      console.log('=== Form Data ===');
      for (let [key, value] of formData.entries()) {
        if (key.includes('properties')) {
          console.log(`${key}: "${value}"`);
        }
      }
    }
  };
  
  console.log('💡 Debug: Use window.debugPersonalization() to check personalization values');
}